import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { ShoppingBag, Check } from "lucide-react";

export default function ProductDetail() {
  const { id } = useParams();
  const { addItem } = useCart();
  const [p, setP] = useState(null);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => { api.get(`/products/${id}`).then((r) => setP(r.data)); }, [id]);

  if (!p) return <div className="pt-32 text-center text-zinc-500 text-mono">Loading…</div>;

  const isWheels = p.category === "wheels";
  const soldOut = p.status === "sold_out";
  const comingSoon = p.status === "coming_soon";

  const handleAdd = () => {
    if (soldOut || comingSoon) return;
    const variant = {};
    if (p.sizes?.length) variant.size = size || p.sizes[0];
    if (p.colors?.length) variant.color = color || p.colors[0];
    addItem(p, Object.keys(variant).length ? variant : null);
  };

  return (
    <div data-testid={`product-detail-${p.id}`} className="bg-ink text-white pt-24 pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-12">
        <div>
          <div className="aspect-[4/5] bg-carbon overflow-hidden">
            <img src={p.images?.[activeImg]} alt={p.name} className="w-full h-full object-cover" />
          </div>
          {p.images?.length > 1 && (
            <div className="grid grid-cols-4 gap-1 mt-1">
              {p.images.map((img, i) => (
                <button key={img} onClick={() => setActiveImg(i)} className={`aspect-square overflow-hidden border ${i === activeImg ? "border-adrenaline" : "border-transparent"}`}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <div className="text-mono text-xs uppercase tracking-[0.4em] text-adrenaline">{isWheels ? "Wheel Drop" : "Apparel"}</div>
          <h1 className="text-display text-5xl md:text-7xl uppercase leading-none mt-3">{p.name}</h1>
          <div className="text-display text-3xl mt-6">${p.price.toFixed(2)}</div>

          <div className="flex gap-2 mt-6">
            <Badge status={p.status} />
            {p.limited && <span className="px-3 py-1 text-mono text-[10px] uppercase tracking-[0.25em] border border-adrenaline/60 text-adrenaline bg-black/50">Limited · {p.inventory} units</span>}
          </div>

          <p className="text-zinc-300 leading-relaxed mt-8">{p.description}</p>

          {isWheels && (
            <dl className="grid grid-cols-2 gap-x-6 gap-y-4 mt-10 text-mono text-sm">
              {p.size && <Spec k="Size" v={p.size} />}
              {p.bolt_pattern && <Spec k="Bolt Pattern" v={p.bolt_pattern} />}
              {p.offset && <Spec k="Offset" v={p.offset} />}
              {p.backspacing && <Spec k="Backspacing" v={p.backspacing} />}
              {p.finish && <Spec k="Finish" v={p.finish} />}
              {p.beadlock !== null && p.beadlock !== undefined && <Spec k="Beadlock" v={p.beadlock ? "Yes" : "No"} />}
            </dl>
          )}

          {!isWheels && (
            <div className="mt-8 space-y-6">
              {p.sizes?.length > 0 && (
                <div>
                  <div className="text-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">Size</div>
                  <div className="flex gap-2 flex-wrap">
                    {p.sizes.map((s) => (
                      <button key={s} data-testid={`size-${s}`} onClick={() => setSize(s)} className={`px-4 py-2 border text-mono text-sm ${size === s ? "border-white bg-white text-black" : "border-white/20 text-white hover:border-white"}`}>{s}</button>
                    ))}
                  </div>
                </div>
              )}
              {p.colors?.length > 0 && (
                <div>
                  <div className="text-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">Color</div>
                  <div className="flex gap-2 flex-wrap">
                    {p.colors.map((c) => (
                      <button key={c} data-testid={`color-${c}`} onClick={() => setColor(c)} className={`px-4 py-2 border text-mono text-sm ${color === c ? "border-white bg-white text-black" : "border-white/20 text-white hover:border-white"}`}>{c}</button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <button
            data-testid="add-to-cart-btn"
            disabled={soldOut || comingSoon}
            onClick={handleAdd}
            className="mt-10 w-full py-5 bg-adrenaline text-white text-heading uppercase tracking-[0.2em] text-sm hover:bg-red-600 transition-colors flex items-center justify-center gap-3 btn-glow disabled:opacity-40 disabled:cursor-not-allowed disabled:bg-zinc-800 disabled:shadow-none"
          >
            {soldOut ? "Sold Out" : comingSoon ? "Coming Soon" : (<><ShoppingBag size={16} /> Pre-Order · Ships 3–5 Weeks</>)}
          </button>

          <div className="grid grid-cols-3 gap-4 mt-8 text-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            <div className="flex items-center gap-2"><Check size={12} className="text-adrenaline" /> Texas Built</div>
            <div className="flex items-center gap-2"><Check size={12} className="text-adrenaline" /> Pre-Order Only</div>
            <div className="flex items-center gap-2"><Check size={12} className="text-adrenaline" /> 30-Day Returns</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Spec({ k, v }) {
  return (
    <div>
      <dt className="text-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">{k}</dt>
      <dd className="text-white text-mono text-sm mt-1">{v}</dd>
    </div>
  );
}

function Badge({ status }) {
  const m = {
    available: { c: "border-white/20 text-white", t: "Available" },
    coming_soon: { c: "border-white/40 text-white", t: "Coming Soon" },
    sold_out: { c: "border-adrenaline/60 text-adrenaline", t: "Sold Out" },
  };
  const s = m[status] || m.available;
  return <span className={`px-3 py-1 text-mono text-[10px] uppercase tracking-[0.25em] border ${s.c} bg-black/50 backdrop-blur-md`}>{s.t}</span>;
}
